const financialAccounts = {
  list: {
    title: 'Financial Accounts',
    search: {
      placeholder: 'Search accounts...',
      label: 'Search accounts',
    },
    typeFilter: {
      label: 'Account type filter',
      placeholder: 'Select account type',
      options: {
        all: 'All Types',
        cash: 'Cash',
        checking: 'Checking',
        savings: 'Savings',
      },
    },
    buttons: {
      newAccount: 'New Account',
      syncTransactions: 'Sync Transactions',
      edit: 'Edit',
    },
    status: {
      hiddenForPurchases: 'Hidden for Purchases',
      lastUpdate: 'Last update',
    },
    empty: {
      description: 'No financial accounts created yet',
      createPrompt: 'Click the Create Account button below to get started.',
      button: 'Create Account',
    },
    viewTransactions: 'View Transactions',
    viewDisable: 'View Disable',
    reconcile: 'Reconcile',
    tooltips: {
      linked: 'Linked to Plaid',
      notLinked: 'Not linked to Plaid',
      visibleInPayments: 'Visible in payments',
      hiddenFromPayments: 'Hidden from payments',
    },
    selection: {
      singular: 'account selected',
      plural: 'accounts selected',
    },
  },
  transactions: {
    drawer: {
      title: (accountName: string) => `${accountName} - Transactions`,
      empty: {
        title: 'No payments found',
        description:
          'There are no payments recorded for this financial account.',
      },
    },
  },
  errors: {
    loadingAccounts: (message: string) =>
      `Error loading financial accounts: ${message}`,
  },
  dialog: {
    title: {
      create: 'New Account',
      edit: 'Edit Account',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Account',
      save: 'Save Changes',
    },
  },
  form: {
    fields: {
      accountName: 'Account Name',
      accountType: 'Account Type',
      accountTypePlaceholder: 'Select Account Type',
      facility: 'Facility',
      initialBalance: 'Initial Balance',
      initialBalanceDisabled:
        'Initial balance cannot be changed after creation',
      roundingMethod: 'Rounding Method',
      roundingMethodPlaceholder: 'Select Rounding Method',
      roundingPrecision: 'Rounding Precision',
      roundingPrecisionPlaceholder: 'Select Rounding Precision',
      visibleInPayments: 'Make this account available for payments',
      nachaConfig: 'NACHA Configuration',
      nachaCompanyIdentification: 'Company Identification',
      nachaCompanyName: 'Company Name',
      paymentAllocationMethod: 'Payment Allocation Method',
      paymentAllocationMethodPlaceholder: 'Select Payment Allocation Method',
      nachaDefaultSecCode: 'Default SEC Code',
      nachaDefaultSecCodePlaceholder: 'Select SEC Code',
    },
  },
  plaid: {
    linkButton: 'Link Accounts',
    facilityDialog: {
      title: 'Assign Facility',
      description: 'Select a facility for your linked accounts',
      facilityLabel: 'Facility',
      applyToAll: 'Apply same facility to all accounts',
      accountsToConnect: 'Accounts to connect:',
      connect: 'Connect Accounts',
      cancel: 'Cancel',
    },
    messages: {
      syncSuccess: 'Accounts synced successfully',
      syncError: 'Failed to sync accounts',
      initError: 'Failed to initialize Plaid Link',
      connectSuccess: 'Bank account connected successfully',
      connectError: 'Failed to connect bank account.',
    },
  },
  reconcile: {
    title: 'Reconcile Cash Drawer',
    fields: {
      physicalCount: 'Physical Count',
      notes: 'Notes',
    },
    buttons: {
      cancel: 'Cancel',
      reconcile: 'Reconcile',
    },
    messages: {
      success: 'Cash drawer reconciled successfully',
      error: (message: string) => `Failed to reconcile: ${message}`,
    },
  },
  checkTemplate: {
    title: 'Check Template Settings',
    sections: {
      layout: 'Layout',
      displayOptions: 'Display Options',
      signatures: 'Signature Settings',
      font: 'Font Settings',
    },
    fields: {
      checkType: 'Check Type',
      checkLayout: 'Check Layout',
      displayPayeeName: 'Payee Name',
      displayPayeeAddress: 'Payee Address',
      displayDate: 'Date',
      displayAmount: 'Amount',
      displayAmountWords: 'Amount in Words',
      displayMemo: 'Memo',
      displayAccountNumber: 'Account Number',
      displayBankInfo: 'Bank Info',
      displaySignature: 'Signature',
      displaySecondSignature: 'Second Signature',
      displayCompanyName: 'Company Name',
      displayCompanyAddress: 'Company Address',
      displayCompanyPhone: 'Company Phone',
      displayCheckNumber: 'Check Number',
      displayMicr: 'MICR Line',
      minAmountSignature: 'Min Amount for Signature',
      maxAmountSecondSignature: 'Max Amount for Second Signature',
      checkFont: 'Font',
      checkFontSize: 'Font Size',
    },
    checkTypes: {
      preprintedPaper: 'Preprinted Paper',
      blankPaper: 'Blank Paper',
    },
    checkLayouts: {
      checkAtTop: 'Check at Top',
      checkAtMiddle: 'Check at Middle',
      checkAtBottom: 'Check at Bottom',
    },
    buttons: {
      cancel: 'Cancel',
      save: 'Save Settings',
    },
    messages: {
      success: 'Check template settings saved',
      error: (message: string) => `Failed to save settings: ${message}`,
    },
  },
  positivePay: {
    title: 'Generate Positive Pay File',
    fields: {
      template: 'Template',
      financialAccount: 'Financial Account',
      dateFrom: 'From Date',
      dateTo: 'To Date',
    },
    buttons: {
      cancel: 'Cancel',
      generate: 'Generate File',
    },
    messages: {
      success: 'Positive pay file generated and downloaded',
      error: (message: string) => `Failed to generate file: ${message}`,
    },
  },
};

const overview = {
  title: 'Finance Overview',
  subtitle: 'Overview of your financial operations',
  kpi: {
    pendingBills: 'Pending Bills',
    pendingInvoices: 'Pending Invoices',
    totalAP: 'Total AP Outstanding',
    totalAR: 'Total AR Outstanding',
    otherCurrencies: (count: number) =>
      count === 1 ? '+1 other currency' : `+${count} other currencies`,
  },
  financialAccounts: {
    title: 'Financial Accounts',
    subtitle: 'Account balances',
    empty: 'No financial accounts found',
    columns: {
      name: 'Account',
      type: 'Type',
      balance: 'Balance',
    },
  },
  aging: {
    title: 'AR/AP Aging',
    subtitle: 'Overdue breakdown',
    empty: 'No pending bills or invoices',
    buckets: {
      current: 'Current',
      days1to30: '1-30 days',
      days31to60: '31-60 days',
      days61to90: '61-90 days',
      days90plus: '90+ days',
    },
    series: {
      ap: 'AP (Bills)',
      ar: 'AR (Invoices)',
    },
  },
  recentBills: {
    title: 'Recent Bills',
    subtitle: 'Your latest bills and payments',
    empty: 'No bills found',
  },
  recentInvoices: {
    title: 'Recent Invoices',
    subtitle: 'Your latest invoices and receivables',
    empty: 'No invoices found',
  },
  columns: {
    documentNumber: 'Doc #',
    account: 'Account',
    dueDate: 'Due Date',
    amount: 'Amount',
    status: 'Status',
  },
};

const balances = {
  list: {
    title: 'Balances',
    search: {
      placeholder: 'Search accounts...',
      label: 'Search accounts',
    },
    buttons: {
      receive: (currency: string) => `Receive ${currency}`,
      pay: (currency: string) => `Pay ${currency}`,
      more: '...',
    },
    empty: {
      title: 'No balances found',
      description: 'There are no account balances matching your criteria',
    },
    error: (message: string) => `Error fetching balances: ${message}`,
    ariaLabel: 'List of Balances',
    selected: (count: number) =>
      `${count === 1 ? 'account' : 'accounts'} selected`,
  },
  netPay: {
    title: 'Net Pay',
    fields: {
      accountName: 'Account',
      paymentMethod: 'Payment Method',
      financialAccount: 'Financial Account',
      transactionDate: 'Transaction Date',
      checkNumber: 'Check Number',
      checkMemo: 'Check Memo',
      checkFacility: 'Check Facility',
      achNumber: 'ACH Number',
      wireNumber: 'Wire Number',
    },
    buttons: {
      cancel: 'Cancel',
      submit: 'Create Net Payment',
    },
    result: {
      payableTotal: 'Payable Total',
      receivableTotal: 'Receivable Total',
      netAmount: 'Net Amount',
    },
    messages: {
      success: 'Net payment created successfully',
    },
  },
};

const bills = {
  list: {
    title: 'Bills',
    search: {
      placeholder: 'Search bills...',
      label: 'Search bills',
      facilityPlaceholder: 'Search facilities...',
    },
    status: {
      label: 'Status',
      options: {
        all: 'All Statuses',
        pending: 'Pending',
        paid: 'Paid',
        voided: 'Voided',
      },
      badge: {
        pending: 'Pending',
        paid: 'Paid',
        voided: 'Voided',
      },
    },
    columns: {
      facility: 'Facility',
    },
    toolbar: {
      selected: 'selected',
      pay: (count: number) => `Pay ${count}`,
      void: (count: number) => `Void ${count}`,
      voidSuccess: (count: number) =>
        count === 1
          ? 'Successfully voided 1 bill'
          : `Successfully voided ${count} bills`,
      voidPartial: (succeeded: number, total: number) =>
        `${succeeded} of ${total} bills voided. ${total - succeeded} failed.`,
      voidFailed: 'Failed to void bills',
    },
    buttons: {
      pay: 'Pay',
      void: 'Void',
      more: '...',
      viewDetails: 'View Details',
      payMultiple: ({ count, type }: { count: number; type: string }) =>
        `Pay ${count} ${type}`,
      create: 'Create Bill',
    },
    tooltips: {
      notAllPending: 'Only pending bills can be paid',
      cannotVoid: 'Only pending bills can be voided',
      differentAccounts: 'Selected bills must be from the same account',
      differentFacilities: 'Selected bills must be from the same facility',
    },
    error: (message: string) => `Error loading bills: ${message}`,
    viewModes: { all: 'All', byAccount: 'By Account' },
    groupHeader: {
      unknownAccount: 'Unknown Account',
      billCount: (count: number) => (count === 1 ? '1 bill' : `${count} bills`),
      outstanding: (total: string) => `${total} outstanding`,
    },
    documentNumber: (number: number | string) => `BILL-${number}`,
  },
  detail: {
    title: 'Bill Details',
    tabs: {
      info: 'Info',
      lineItems: 'Line Items',
      payments: 'Payments',
    },
    fields: {
      type: 'Type',
      status: 'Status',
      facility: 'Facility',
      documentNumber: 'Document Number',
      documentDate: 'Document Date',
      dueDate: 'Due Date',
      amount: 'Amount',
      openAmount: 'Open Amount',
      currency: 'Currency',
      paymentTerm: 'Payment Term',
      notes: 'Notes',
      eligibleAt: 'Eligible At',
      preferredPaymentMethod: 'Preferred Payment Method',
    },
    sections: {
      basicInfo: 'Basic Info',
      payments: 'Payments',
    },
    actions: {
      void: 'Void Bill',
      markAsPaid: 'Mark as Paid',
    },
    messages: {
      voided: 'Bill has been voided successfully',
      voidError: (message: string) => `Failed to void bill: ${message}`,
      paymentRemoved: 'Payment removed successfully',
      removePaymentError: (message: string) =>
        `Failed to remove payment: ${message}`,
      updateError: (message: string) => `Failed to update bill: ${message}`,
    },
    empty: {
      lineItems: 'No line items for this bill',
      noData: 'No bill data available.',
    },
    payments: {
      empty: 'No payments applied to this bill.',
      applyPayment: 'Apply Payment',
      columns: {
        paymentNumber: 'Payment #',
        appliedAmount: 'Applied',
        date: 'Date',
      },
    },
    unsavedChanges: {
      message: 'You have unsaved changes',
      discard: 'Discard',
      save: 'Save Changes',
    },
    footer: {
      totalAmount: 'Total Amount',
      totalPaid: 'Total Paid',
      numberOfPayments: '# of Payments',
      openAmount: 'Open Amount',
    },
  },
  wizard: {
    steps: {
      basicInfo: 'Basic Info',
      lineItems: 'Line Items',
    },
    fields: {
      documentNumber: 'Document Number',
      documentNumberDescription: 'Auto-generated on creation',
      billType: 'Bill Type',
      facility: 'Facility',
      account: 'Account',
      documentDate: 'Document Date',
      eligibleAt: 'Eligible At',
      paymentTerm: 'Payment Term',
      paymentMethod: 'Payment Method',
      currency: 'Currency',
      notes: 'Notes',
    },
    buttons: {
      back: 'Back',
      cancel: 'Cancel',
      create: 'Create Bill',
      next: 'Next',
    },
    validation: {
      billTypeRequired: 'Bill type is required',
      documentDateRequired: 'Document date is required',
      facilityRequired: 'Facility is required',
      paymentTermRequired: 'Payment term is required',
      paymentMethodRequired: 'Payment method is required',
    },
    messages: {
      createSuccess: 'Bill created successfully',
      createError: (message: string) => `Failed to create bill: ${message}`,
    },
  },
  lineItems: {
    title: 'Line Items',
    addButton: 'Add Line Item',
    ariaLabel: 'Bill line items',
    empty: 'No line items for this bill.',
    fields: {
      description: 'Description',
      descriptionPlaceholder: 'Description',
      quantity: 'Quantity',
      unit: 'Unit',
      unitPlaceholder: 'Unit',
      unitPrice: 'Unit Price',
      total: 'Total',
      groupName: 'Group',
      groupNamePlaceholder: 'Select or create group...',
    },
  },
  paymentDrawer: {
    cannotProcess: {
      title: 'Cannot Process Payment',
      message:
        'Multiple bills from different accounts cannot be paid simultaneously. Please select bills from the same account.',
      differentFacilities:
        'Multiple bills from different facilities cannot be paid simultaneously. Please select bills from the same facility.',
      close: 'Close',
    },
    title: {
      single: 'Pay Bill',
      multiple: 'Pay Multiple Bills',
    },
    fields: {
      payee: 'Payee',
      billNumber: 'Bill Number',
      bills: 'Bills',
      billsSelected: (count: number) => `${count} bills selected`,
      totalAmount: 'Total Amount',
      paymentAmount: 'Payment Amount',
      originalTotal: (amount: string) => `Total due: ${amount}`,
      paymentMethod: 'Payment Method',
      transactionDate: 'Transaction Date',
      financialAccount: 'Financial Account',
      checkFacility: 'Check Facility',
      checkNumber: 'Check Number',
      startingCheckNumber: 'Starting Check Number',
      checkMemo: 'Check Memo',
      achNumber: 'ACH Number',
      wireNumber: 'Wire Number',
      due: 'due',
      duePrefix: 'Due',
      noDueDate: 'No due date',
    },
    buttons: {
      cancel: 'Cancel',
      pay: 'Pay Now',
    },
    allocationBanner: {
      message: (method: string) => {
        const names: Record<string, string> = {
          FIFO: 'FIFO',
          LIFO: 'LIFO',
          WeightedDistribution: 'Weighted Distribution',
        };
        return `This account uses ${names[method] ?? method} allocation.`;
      },
      apply: 'Apply',
      keepCurrent: 'Keep Current',
    },
    messages: {
      success: 'Payment created and applied successfully',
      error: (message: string) => `Failed to process payment: ${message}`,
      partialSuccess: (succeeded: number, total: number) =>
        `${succeeded} of ${total} payments succeeded. ${total - succeeded} failed.`,
      mixedCurrency:
        'Batch payments across different currencies are not supported. Please pay each currency group separately.',
      checkAutoIncrement: (start: string, end: string) =>
        `Check numbers will be assigned ${start} through ${end}.`,
    },
  },
  applyPaymentDialog: {
    fields: {
      selectPayment: 'Payment',
      selectPaymentPlaceholder: 'Select a payment...',
    },
    loading: 'Loading payments...',
    noPaymentsAvailable: 'No payments available',
    createNewPayment: 'Create New Payment',
    unknownAccount: 'Unknown Account',
    availableSuffix: 'available',
    paymentAvailable: 'Payment available',
    billOpen: 'Bill open amount',
  },
};

const invoices = {
  list: {
    title: 'Invoices',
    search: {
      placeholder: 'Search invoices...',
      label: 'Search invoices',
    },
    status: {
      label: 'Status',
      options: {
        all: 'All Statuses',
        proForma: 'Pro Forma',
        pending: 'Pending',
        paid: 'Paid',
        voided: 'Voided',
      },
      badge: {
        proForma: 'Pro Forma',
        pending: 'Pending',
        paid: 'Paid',
        voided: 'Voided',
      },
    },
    columns: {
      facility: 'Facility',
    },
    toolbar: {
      selected: 'selected',
      receive: (count: number) => `Receive ${count}`,
      void: (count: number) => `Void ${count}`,
      voidSuccess: (count: number) =>
        count === 1
          ? 'Successfully voided 1 invoice'
          : `Successfully voided ${count} invoices`,
      voidPartial: (succeeded: number, total: number) =>
        `${succeeded} of ${total} invoices voided. ${total - succeeded} failed.`,
      voidFailed: 'Failed to void invoices',
    },
    buttons: {
      receive: 'Receive',
      void: 'Void',
      more: '...',
      viewDetails: 'View Details',
      receiveMultiple: (count: number) =>
        count === 0
          ? 'Receive'
          : count === 1
            ? `Receive ${count} Invoice`
            : `Receive ${count} Invoices`,
      create: 'Create Invoice',
    },
    tooltips: {
      notAllPending: 'Only pending invoices can receive payments',
      cannotVoid: 'Only pending or pro forma invoices can be voided',
      differentAccounts: 'Selected invoices must be from the same account',
      differentFacilities: 'Selected invoices must be from the same facility',
    },
    error: (message: string) => `Error loading invoices: ${message}`,
    viewModes: { all: 'All', byAccount: 'By Account' },
    groupHeader: {
      unknownAccount: 'Unknown Account',
      invoiceCount: (count: number) =>
        count === 1 ? '1 invoice' : `${count} invoices`,
      outstanding: (total: string) => `${total} outstanding`,
    },
    documentNumber: (number: number | string) => `INV-${number}`,
  },
  detail: {
    title: 'Invoice Details',
    tabs: {
      info: 'Info',
      lineItems: 'Line Items',
      payments: 'Payments',
    },
    fields: {
      type: 'Type',
      status: 'Status',
      facility: 'Facility',
      documentNumber: 'Document Number',
      documentDate: 'Document Date',
      dueDate: 'Due Date',
      amount: 'Amount',
      openAmount: 'Open Amount',
      currency: 'Currency',
      paymentTerm: 'Payment Term',
      notes: 'Notes',
    },
    actions: {
      void: 'Void Invoice',
      markAsPaid: 'Mark as Paid',
    },
    messages: {
      voided: 'Invoice has been voided successfully',
      voidError: (message: string) => `Failed to void invoice: ${message}`,
      paymentRemoved: 'Payment removed successfully',
      removePaymentError: (message: string) =>
        `Failed to remove payment: ${message}`,
      updateError: (message: string) => `Failed to update invoice: ${message}`,
    },
    empty: {
      lineItems: 'No line items for this invoice',
      noData: 'No invoice data available.',
    },
    payments: {
      empty: 'No payments applied to this invoice.',
      applyPayment: 'Apply Payment',
      columns: {
        paymentNumber: 'Payment #',
        appliedAmount: 'Applied',
        date: 'Date',
      },
    },
    sections: {
      basicInfo: 'Basic Info',
    },
    unsavedChanges: {
      message: 'You have unsaved changes',
      discard: 'Discard',
      save: 'Save Changes',
    },
    footer: {
      totalAmount: 'Total Amount',
      totalPaid: 'Total Paid',
      numberOfPayments: '# of Payments',
      openAmount: 'Open Amount',
    },
  },
  wizard: {
    steps: {
      basicInfo: 'Basic Info',
      lineItems: 'Line Items',
    },
    fields: {
      documentNumber: 'Document Number',
      documentNumberDescription: 'Auto-generated on creation',
      invoiceType: 'Invoice Type',
      facility: 'Facility',
      account: 'Account',
      documentDate: 'Document Date',
      dueDate: 'Due Date',
      paymentTerm: 'Payment Term',
      currency: 'Currency',
      notes: 'Notes',
    },
    buttons: {
      back: 'Back',
      cancel: 'Cancel',
      create: 'Create Invoice',
      next: 'Next',
    },
    validation: {
      invoiceTypeRequired: 'Invoice type is required',
      documentDateRequired: 'Document date is required',
      facilityRequired: 'Facility is required',
      accountRequired: 'Account is required',
      paymentTermRequired: 'Payment term is required',
    },
    messages: {
      createSuccess: 'Invoice created successfully',
      createError: (message: string) => `Failed to create invoice: ${message}`,
    },
  },
  lineItems: {
    title: 'Line Items',
    addButton: 'Add Line Item',
    ariaLabel: 'Invoice line items',
    empty: 'No line items for this invoice.',
    fields: {
      description: 'Description',
      descriptionPlaceholder: 'Description',
      quantity: 'Quantity',
      unit: 'Unit',
      unitPlaceholder: 'Unit',
      unitPrice: 'Unit Price',
      total: 'Total',
      groupName: 'Group',
      groupNamePlaceholder: 'Select or create group...',
    },
  },
  paymentDrawer: {
    cannotProcess: {
      title: 'Cannot Process Payment',
      message:
        'Multiple invoices from different accounts cannot be paid simultaneously. Please select invoices from the same account.',
      differentFacilities:
        'Multiple invoices from different facilities cannot be paid simultaneously. Please select invoices from the same facility.',
      close: 'Close',
    },
    title: {
      single: 'Receive Payment',
      multiple: 'Receive Multiple Payments',
    },
    fields: {
      customer: 'Customer',
      invoiceNumber: 'Invoice Number',
      invoices: 'Invoices',
      invoicesSelected: (count: number) => `${count} invoices selected`,
      totalAmount: 'Total Amount',
      paymentAmount: 'Payment Amount',
      originalTotal: (amount: string) => `Total due: ${amount}`,
      paymentMethod: 'Payment Method',
      transactionDate: 'Transaction Date',
      financialAccount: 'Financial Account',
      checkFacility: 'Check Facility',
      checkNumber: 'Check Number',
      startingCheckNumber: 'Starting Check Number',
      checkMemo: 'Check Memo',
      achNumber: 'ACH Number',
      wireNumber: 'Wire Number',
      due: 'due',
      duePrefix: 'Due',
      noDueDate: 'No due date',
    },
    buttons: {
      cancel: 'Cancel',
      receive: 'Receive Now',
    },
    allocationBanner: {
      message: (method: string) => {
        const names: Record<string, string> = {
          FIFO: 'FIFO',
          LIFO: 'LIFO',
          WeightedDistribution: 'Weighted Distribution',
        };
        return `This account uses ${names[method] ?? method} allocation.`;
      },
      apply: 'Apply',
      keepCurrent: 'Keep Current',
    },
    messages: {
      success: 'Payment received and applied successfully',
      error: (message: string) => `Failed to process payment: ${message}`,
      partialSuccess: (succeeded: number, total: number) =>
        `${succeeded} of ${total} payments succeeded. ${total - succeeded} failed.`,
      mixedCurrency:
        'Batch payments across different currencies are not supported. Please pay each currency group separately.',
      checkAutoIncrement: (start: string, end: string) =>
        `Check numbers will be assigned ${start} through ${end}.`,
    },
  },
  applyPaymentDialog: {
    fields: {
      selectPayment: 'Payment',
      selectPaymentPlaceholder: 'Select a payment...',
    },
    loading: 'Loading payments...',
    noPaymentsAvailable: 'No payments available',
    createNewPayment: 'Create New Payment',
    unknownAccount: 'Unknown Account',
    availableSuffix: 'available',
    paymentAvailable: 'Payment available',
    invoiceOpen: 'Invoice open amount',
  },
};

const lineItems = {
  columns: {
    description: 'Description',
    quantity: 'Quantity',
    unit: 'Unit',
    unitPrice: 'Unit Price',
    totalPrice: 'Total Price',
  },
  placeholders: {
    selectItem: 'Select item...',
    enterDescription: 'Enter description...',
  },
  addButton: 'Add Line Item',
  footer: {
    items: (count: number) => (count === 1 ? '1 item' : `${count} items`),
  },
};

const payments = {
  list: {
    title: 'Payments',
    search: {
      placeholder: 'Search payments...',
      label: 'Search payments',
    },
    status: {
      options: {
        issued: 'Issued',
        posted: 'Posted',
        voided: 'Voided',
      },
    },
    methodFilter: {
      label: 'Method',
      options: {
        Check: 'Check',
        ACH: 'ACH',
        Wire: 'Wire',
        Cash: 'Cash',
        ATM: 'ATM',
      } as Record<string, string>,
    },
    dateFilter: {
      label: 'Date Range',
      clear: 'Clear',
      done: 'Done',
    },
    columns: {
      financialAccount: 'Financial Account',
      linkedDocuments: 'Linked Documents',
      linkedDocumentsMore: ({ count }: { count: number }) => `+${count} more`,
    },
    toolbar: {
      selected: 'selected',
      generateAchFile: 'Generate ACH File',
      printChecks: 'Print Checks',
    },
    bulkActions: {
      confirm: 'Confirm',
      unconfirm: 'Unconfirm',
      confirmSuccess: ({ count }: { count: number }) =>
        `Successfully confirmed ${count} payments`,
      unconfirmSuccess: ({ count }: { count: number }) =>
        `Successfully unconfirmed ${count} payments`,
      partialFailure: ({
        failedCount,
        totalCount,
      }: {
        failedCount: number;
        totalCount: number;
      }) => `${failedCount} of ${totalCount} payments failed`,
    },
    buttons: {
      applyAdvance: 'Apply Advance',
      viewDetails: 'View Details',
      create: 'Create Payment',
    },
    error: (message: string) => `Error loading payments: ${message}`,
  },
  detail: {
    title: 'Payment Details',
    createdOn: 'Created on',
    updatedOn: 'Updated on',
    tabs: {
      info: 'Info',
      linkedDocuments: 'Linked Documents',
    },
    fields: {
      documentNumber: 'Document Number',
      status: 'Status',
      paymentType: 'Payment Type',
      transactionDate: 'Transaction Date',
      amount: 'Amount',
      unappliedAmount: 'Unapplied Amount',
      paymentMethod: 'Payment Method',
      account: 'Account',
      financialAccount: 'Financial Account',
      checkNumber: 'Check Number',
      checkMemo: 'Check Memo',
      achNumber: 'ACH Number',
      wireNumber: 'Wire Number',
    },
    actions: {
      void: 'Void Payment',
      applyToBill: 'Apply to Bill',
      applyToInvoice: 'Apply to Invoice',
    },
    messages: {
      updateSuccess: 'Payment updated successfully',
      updateError: (message: string) => `Failed to update payment: ${message}`,
      loadError: (message: string) => `Error loading payment: ${message}`,
      voidSuccess: 'Payment has been voided successfully',
      voidError: (message: string) => `Failed to void payment: ${message}`,
      paymentApplied: 'Payment applied successfully',
      paymentRemoved: 'Payment link removed successfully',
      applyError: (message: string) => `Failed to apply payment: ${message}`,
      removeError: (message: string) =>
        `Failed to remove payment link: ${message}`,
    },
    linkedDocuments: {
      billNumber: 'Bill Number',
      invoiceNumber: 'Invoice Number',
      appliedAmount: 'Applied Amount',
      appliedAt: 'Applied At',
      linkedBills: 'Linked Bills',
      linkedInvoices: 'Linked Invoices',
      emptyState: 'No bills or invoices linked to this payment.',
      removeBill: 'Remove bill',
      removeInvoice: 'Remove invoice',
    },
    voidDialog: {
      title: 'Void Payment',
      message:
        'Are you sure you want to void this payment? This action cannot be undone.',
      cascadeEffects: {
        unlinkBills: (count: number) =>
          `${count} bill(s) will be unlinked from this payment`,
        unlinkInvoices: (count: number) =>
          `${count} invoice(s) will be unlinked from this payment`,
        restoreBalance: 'The financial account balance will be restored',
      },
      buttons: {
        cancel: 'Cancel',
        void: 'Void Payment',
      },
    },
    unsavedChanges: {
      message: 'You have unsaved changes',
      discard: 'Discard',
      save: 'Save Changes',
    },
    sections: {
      basicInfo: 'Basic Info',
      checkDetails: 'Check Details',
      achDetails: 'ACH Details',
      wireDetails: 'Wire Details',
    },
    footer: {
      totalAmount: 'Total Amount',
      totalApplied: 'Total Applied',
      linkedDocuments: 'Linked Documents',
      unappliedAmount: 'Unapplied Amount',
    },
    empty: {
      noData: 'No payment data available.',
    },
    dialogs: {
      applyToBill: {
        title: 'Apply Payment to Bill',
        billLabel: 'Select Bill',
        selectBill: 'Choose a bill...',
        amountLabel: 'Amount to Apply',
        unappliedBalance: 'Unapplied balance',
        billAmount: 'Bill amount',
        openBalance: 'Open balance',
        noBillsAvailable: 'No open bills available for this account',
        unknownAccount: 'Unknown',
        openSuffix: 'open',
        loading: 'Loading...',
      },
      applyToInvoice: {
        title: 'Apply Payment to Invoice',
        invoiceLabel: 'Select Invoice',
        selectInvoice: 'Choose an invoice...',
        amountLabel: 'Amount to Apply',
        unappliedBalance: 'Unapplied balance',
        invoiceAmount: 'Invoice amount',
        openBalance: 'Open balance',
        noInvoicesAvailable: 'No open invoices available for this account',
        unknownAccount: 'Unknown',
        openSuffix: 'open',
        loading: 'Loading...',
      },
    },
  },
  wizard: {
    fields: {
      documentNumber: 'Document Number',
      documentNumberDescription: 'Auto-generated on creation',
      paymentType: 'Payment Type',
      paymentMethod: 'Payment Method',
      amount: 'Amount',
      currency: 'Currency',
      transactionDate: 'Transaction Date',
      account: 'Account',
      financialAccount: 'Financial Account',
      checkFacility: 'Check Facility',
      checkNumber: 'Check Number',
      checkMemo: 'Check Memo',
      achNumber: 'ACH Number',
      wireNumber: 'Wire Number',
    },
    buttons: {
      cancel: 'Cancel',
      create: 'Create Payment',
    },
    messages: {
      createSuccess: 'Payment created successfully',
      createError: (message: string) => `Failed to create payment: ${message}`,
    },
    validation: {
      paymentTypeRequired: 'Payment type is required',
      paymentMethodRequired: 'Payment method is required',
      amountRequired: 'Amount is required and must be greater than 0',
      transactionDateRequired: 'Transaction date is required',
    },
  },
  advances: {
    apply: {
      title: 'Apply Advance',
      description: 'Apply this advance payment to a bill or invoice',
      fields: {
        amount: 'Amount to Apply',
        document: 'Apply To',
      },
      buttons: {
        apply: 'Apply Advance',
        cancel: 'Cancel',
      },
      messages: {
        success: 'Advance applied successfully',
        error: (message: string) => `Failed to apply advance: ${message}`,
      },
    },
  },
  achFile: {
    title: 'Generate ACH File',
    description: ({ count }: { count: number }) =>
      `Generate a NACHA/ACH file for ${count} selected payment(s).`,
    fields: {
      financialAccount: 'Financial Account',
      effectiveEntryDate: 'Effective Entry Date',
    },
    buttons: {
      cancel: 'Cancel',
      generate: 'Generate ACH File',
    },
    messages: {
      success: 'ACH file generated and downloaded',
      error: (message: string) => `Failed to generate ACH file: ${message}`,
    },
  },
  printChecks: {
    title: 'Print Checks',
    description: ({ count }: { count: number }) =>
      `Generate a printable PDF for ${count} selected check payment(s).`,
    buttons: {
      cancel: 'Cancel',
      print: 'Print Checks',
    },
    messages: {
      success: 'Check PDF generated successfully',
      error: (message: string) => `Failed to generate check PDF: ${message}`,
    },
  },
};

const columns = {
  billNumber: 'Bill #',
  supplier: 'Supplier',
  transactionDate: 'Transaction Date',
  dueDate: 'Due Date',
  payee: 'Payee',
  payeeType: 'Payee Type',
  balance: 'Balance',
  balanceOwed: 'Balance Owed',
  currency: 'Currency',

  invoiceNumber: 'Invoice #',
  documentDate: 'Document Date',
  account: 'Account',
  accountType: 'Account Type',

  status: 'Status',
  amount: 'Amount',

  date: 'Date',
  type: 'Type',
  method: 'Method',
  unapplied: 'Unapplied',
  documentNumber: 'Doc #',

  accountName: 'Account Name',
  pendingTransactions: '# of Transactions',
  balancesColumn: 'Balances',
};

const messages = {
  common: {
    loading: 'Loading...',
    unknownError: 'Unknown error',
  },
  bills: {
    voidSuccess: 'Bill has been voided successfully',
    voidError: (message: string) => `Failed to void bill: ${message}`,
    markAsPaidSuccess: 'Bill has been marked as paid successfully',
    markAsPaidError: (message: string) =>
      `Failed to mark bill as paid: ${message}`,
    createSuccess: 'Bill created successfully',
    updateSuccess: 'Bill updated successfully',
    loadError: (message: string) => `Error loading bill: ${message}`,
  },
  invoices: {
    voidSuccess: 'Invoice has been voided successfully',
    voidError: (message: string) => `Failed to void invoice: ${message}`,
    markAsPaidSuccess: 'Invoice has been marked as paid successfully',
    markAsPaidError: (message: string) =>
      `Failed to mark invoice as paid: ${message}`,
    createSuccess: 'Invoice created successfully',
    updateSuccess: 'Invoice updated successfully',
    loadError: (message: string) => `Error loading invoice: ${message}`,
  },
  accounts: {
    createSuccess: 'Account created successfully',
    updateSuccess: 'Account updated successfully',
    saveError: 'Failed to save account',
    facilityRequired: 'Facility is required',
  },
};

const common = {
  buttons: {
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    back: 'Back',
    next: 'Next',
    discard: 'Discard',
    saveChanges: 'Save Changes',
  },
  placeholders: {
    generateNumber: 'Generate number',
  },
};

const enums = {
  billType: {
    Accrual: 'Accrual',
    FreightBill: 'Freight Bill',
    InboundBill: 'Inbound Bill',
    OutboundBill: 'Outbound Bill',
  } as Record<string, string>,
  invoiceType: {
    Accrual: 'Accrual',
    FreightInvoice: 'Freight Invoice',
    InboundInvoice: 'Inbound Invoice',
    OutboundInvoice: 'Outbound Invoice',
  } as Record<string, string>,
  paymentType: {
    Adjustment: 'Adjustment',
    AdvanceIn: 'Advance In',
    AdvanceOut: 'Advance Out',
    Bill: 'Bill Payment',
    Invoice: 'Invoice Receipt',
    Netting: 'Netting',
  } as Record<string, string>,
};

const bankDetails = {
  title: 'Bank Details',
  add: 'Add Bank Detail',
  empty: 'No bank details found',
  fields: {
    bankName: 'Bank Name',
    routingNumber: 'Routing Number',
    accountNumber: 'Account Number',
    accountType: 'Account Type',
    isPrimary: 'Primary Account',
    nickname: 'Nickname',
  },
  accountTypes: {
    checking: 'Checking',
    savings: 'Savings',
  },
  actions: {
    edit: 'Edit',
    delete: 'Delete',
    setAsPrimary: 'Set as Primary',
  },
  dialog: {
    createTitle: 'Add Bank Detail',
    editTitle: 'Edit Bank Detail',
    cancel: 'Cancel',
    save: 'Save',
  },
  messages: {
    created: 'Bank detail added',
    updated: 'Bank detail updated',
    deleted: 'Bank detail deleted',
    error: (message: string) => `Error: ${message}`,
  },
};

const logs = {
  rawPayloadParseError: 'Raw Payload (Parse Error)',
  unknownEventType: 'Unknown Event Type',
  unknownEvent: 'Unknown Event',
  noDataAvailable: 'No data available',
  billDetails: 'Bill Details',
  invoiceDetails: 'Invoice Details',
  bill: 'Bill',
  invoice: 'Invoice',
  deletedBill: 'Deleted Bill',
  deletedInvoice: 'Deleted Invoice',
  statusChange: 'Status Change',
  changes: 'Changes',
  before: 'Before',
  after: 'After',
  events: {
    billCreated: 'Bill Created',
    billUpdated: 'Bill Updated',
    billDeleted: 'Bill Deleted',
    billVoided: 'Bill Voided',
    billPaid: 'Bill Paid',
    invoiceCreated: 'Invoice Created',
    invoiceUpdated: 'Invoice Updated',
    invoiceDeleted: 'Invoice Deleted',
    invoiceVoided: 'Invoice Voided',
    invoicePaid: 'Invoice Paid',
  },
};

export const en = {
  financialAccounts,
  overview,
  balances,
  bills,
  invoices,
  lineItems,
  payments,
  columns,
  messages,
  common,
  enums,
  bankDetails,
  logs,
};
